import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import MaterialFormDialog from './MaterialFormDialog';
import type { MaterialFormValues } from './MaterialFormDialog';
import type { CreateMaterialRequest, MaterialCategoryDto, MaterialDto, PagedResult, UpdateMaterialRequest } from '@/types/materials';
import type { LabelTemplate } from '@/types/labelTemplate';

// LabelTemplate.MaterialId is the single source of truth for "this item's label template" (also
// what the GRN Label Print screen's auto-select reads) — Material itself has no such column, so
// linking/unlinking from here means re-saving the template(s) via the same upsert the Label
// Designer uses, just toggling MaterialId and leaving everything else (canvas, size, ...) as-is.
async function syncLabelTemplateLink(materialId: string, selectedTemplateId: string | null, templates: LabelTemplate[]) {
  const currentlyLinked = templates.find((tpl) => tpl.materialId === materialId);
  if ((currentlyLinked?.id ?? null) === selectedTemplateId) return;

  if (currentlyLinked) {
    await apiClient.post('/label-templates', { ...currentlyLinked, materialId: null, assignmentType: 'Generic' });
  }
  if (selectedTemplateId) {
    const target = templates.find((tpl) => tpl.id === selectedTemplateId);
    if (target) await apiClient.post('/label-templates', { ...target, materialId, assignmentType: 'Material' });
  }
}

export default function MaterialMaster() {
  const { t } = useTranslation('materialMaster');
  const queryClient = useQueryClient();

  const [categoryFilter, setCategoryFilter] = useState('');

  const { data: config } = useProcessConfiguration();
  const { data: categories = [] } = useQuery({
    queryKey: ['material-categories'],
    queryFn: async () => (await apiClient.get<MaterialCategoryDto[]>('/material-categories')).data,
  });
  const { data: labelTemplates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });

  // Server-side pagination is wired on the backend (PagedResult<T>) — the frontend currently
  // requests one large page and lets the shared DataTable handle client-side search/paging on
  // top of it. A true server-driven paginated variant of DataTable is a follow-up once catalog
  // sizes in practice exceed what's comfortable to pull in one request.
  const { data, isLoading } = useQuery({
    queryKey: ['materials', categoryFilter],
    queryFn: async () =>
      (
        await apiClient.get<PagedResult<MaterialDto>>('/materials', {
          params: { page: 1, pageSize: 200, categoryId: categoryFilter || undefined },
        })
      ).data,
  });
  const materials = data?.items ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<MaterialDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MaterialDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['materials'] });

  // Fields the form doesn't surface a UI for yet (not part of the Stitch mockup's visible tabs) —
  // pass through the existing value on edit, or a sensible default on create. labelTemplateId is
  // UI-only (Material has no such column — see syncLabelTemplateLink) and deliberately excluded.
  // A SearchableSelect's "—"/None option yields an EMPTY STRING, but every one of these binds to a
  // Guid? on the API. "" is not a valid Guid, so ASP.NET rejects the entire request with a
  // ValidationProblemDetails before any business logic runs — and because that shape isn't the
  // app's Result envelope, the user just saw "Something went wrong". Normalising every optional id
  // in one place is what stops the next picker added to this form reintroducing it.
  const OPTIONAL_ID_FIELDS = [
    'categoryId', 'supplierId', 'clientId', 'customerId', 'qcTemplateId',
    'defaultLocationId', 'weightProductionLabelTemplateId', 'defaultHardwareStationId',
  ] as const;

  const passthroughFields = (values: MaterialFormValues) => {
    const { labelTemplateId: _labelTemplateId, ...rest } = values;
    const normalisedIds = Object.fromEntries(
      OPTIONAL_ID_FIELDS
        .filter((key) => key in rest)
        .map((key) => [key, (rest as Record<string, unknown>)[key] || null]),
    );
    return {
      ...rest,
      ...normalisedIds,
      manufacturerName: editingMaterial?.manufacturerName ?? null,
      gstRate: editingMaterial?.gstRate ?? null,
      hsnCode: editingMaterial?.hsnCode ?? null,
      serialRequired: editingMaterial?.serialRequired ?? false,
      minOrderQty: editingMaterial?.minOrderQty ?? null,
      boxQty: editingMaterial?.boxQty ?? null,
      palletQty: editingMaterial?.palletQty ?? null,
      pickingStrategyOverride: editingMaterial?.pickingStrategyOverride ?? null,
    };
  };

  const saveMutation = useMutation({
    mutationFn: async (values: MaterialFormValues) => {
      let materialId = editingMaterial?.id;
      if (editingMaterial) {
        const payload: UpdateMaterialRequest = passthroughFields(values);
        await apiClient.put(`/materials/${editingMaterial.id}`, payload);
      } else {
        const payload: CreateMaterialRequest = passthroughFields(values);
        const { data: created } = await apiClient.post<MaterialDto>('/materials', payload);
        materialId = created.id;
      }
      await syncLabelTemplateLink(materialId!, values.labelTemplateId, labelTemplates);
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['label-templates'] });
      setDialogOpen(false);
      setEditingMaterial(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/materials/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<MaterialDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name') },
    { field: 'categoryName', headerName: t('table.category'), render: (r) => r.categoryName ?? '—', filterable: true },
    { field: 'uomCode', headerName: t('table.uom'), filterable: true },
    { field: 'reorderLevel', headerName: t('table.reorderLevel') },
    ...(config?.thirdPartyLogisticsEnabled
      ? [
          { field: 'clientName', headerName: t('table.client'), render: (r: MaterialDto) => r.clientName ?? '—', filterable: true },
          { field: 'customerName', headerName: t('table.customer'), render: (r: MaterialDto) => r.customerName ?? '—', filterable: true },
        ] as DataTableColumn<MaterialDto>[]
      : []),
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingMaterial(r);
                setDialogOpen(true);
              }}
            >
              Edit
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              Delete
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingMaterial(null);
                setDialogOpen(true);
              }}
            >
              {t('addMaterial')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable
        columns={columns}
        rows={materials}
        loading={isLoading}
        searchable
        searchPlaceholder={t('search')}
        getRowId={(r) => r.id}
        exportFileName="materials"
        toolbarActions={
          <TextField select size="small" label={t('table.category')} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('allCategories')}</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      <MaterialFormDialog
        open={dialogOpen}
        material={editingMaterial}
        onClose={() => {
          setDialogOpen(false);
          setEditingMaterial(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteMaterial.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
